import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider } from '@tanstack/react-router'
import { router } from './router'
import { ToastProvider, ToastViewport } from './components/Toast'
import { shouldRetryQuery } from './lib/pollBackoff'
import './i18n' // initialise i18next before the first render (sync, inline resources)
import './styles/index.css'

// Polling defaults live here; per-hook refetchInterval (3s for active jobs,
// stop on terminal) is set in Phase 2 hooks (T027).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry once, but NEVER on a 4xx — retrying a 429 (rate limit) just adds
      // to the storm, and 404/403 won't change on retry. Only network/5xx retry.
      retry: shouldRetryQuery,
      refetchOnWindowFocus: false,
    },
  },
})

const rootEl = document.getElementById('root')
if (!rootEl) {
  throw new Error('#root not found')
}

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
        <ToastViewport />
        {/* Opt-in only: run `VITE_RQ_DEVTOOLS=1 pnpm dev` to show the floating
            devtools toggle. Off by default (dev + prod) so it never covers UI. */}
        {import.meta.env.DEV && import.meta.env.VITE_RQ_DEVTOOLS === '1' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
)
